/**
 * Creative Summary Cards Component
 * 
 * Displays key metrics for creative performance
 * Modern, clean design with subtle color accents
 */

import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { CreativeSummary } from '../types'

interface CreativeSummaryCardsProps {
  summary: CreativeSummary | null
  loading?: boolean
}

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Format number with abbreviation
function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

// Metric card component
function MetricCard({
  title,
  value,
  icon: Icon,
  trend,
  loading,
  className,
}: {
  title: string
  value: string | number
  icon: React.ElementType
  trend?: 'up' | 'down' | 'neutral'
  loading?: boolean
  className?: string
}) {
  if (loading) {
    return (
      <div className={cn('bg-tf-bg-card rounded-xl border border-tf-border p-5', className)}>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="h-4 w-24 bg-tf-bg-hover rounded animate-pulse" />
            <div className="h-8 w-20 bg-tf-bg-hover rounded animate-pulse" />
          </div>
          <div className="h-10 w-10 bg-tf-bg-hover rounded-lg animate-pulse" />
        </div>
      </div>
    )
  }

  return (
    <div className={cn(
      'bg-tf-bg-card rounded-xl border border-tf-border p-5',
      'hover:border-tf-border-sub hover:shadow-sm transition-all duration-200',
      className
    )}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-tf-text-muted mb-1">{title}</p>
          <p className="text-2xl font-semibold text-tf-text tracking-tight">{value}</p>
        </div>
        <div className={cn(
          'p-2.5 rounded-lg',
          trend === 'up' ? 'bg-tf-feedback-success-bg' : trend === 'down' ? 'bg-tf-feedback-error-bg' : 'bg-tf-bg-muted'
        )}>
          <Icon className={cn(
            'w-5 h-5',
            trend === 'up' ? 'text-tf-feedback-success-text' : trend === 'down' ? 'text-tf-feedback-error-text' : 'text-tf-text-muted'
          )} />
        </div>
      </div>
    </div>
  )
}

// Performance status card - modern minimal design
function StatusCard({
  title,
  count,
  icon: Icon,
  accent,
  loading,
}: {
  title: string
  count: number
  icon: React.ElementType
  accent: 'emerald' | 'blue' | 'amber' | 'rose'
  loading?: boolean
}) {
  const accentStyles = {
    emerald: {
      border: 'border-l-emerald-500',
      icon: 'text-tf-feedback-success-text',
      bg: 'bg-tf-feedback-success-bg',
    },
    blue: {
      border: 'border-l-blue-500',
      icon: 'text-tf-feedback-info-text',
      bg: 'bg-tf-feedback-info-bg',
    },
    amber: {
      border: 'border-l-amber-500',
      icon: 'text-tf-feedback-warning-text',
      bg: 'bg-tf-feedback-warning-bg',
    },
    rose: {
      border: 'border-l-rose-500',
      icon: 'text-tf-feedback-error-text',
      bg: 'bg-tf-feedback-error-bg',
    },
  }

  const styles = accentStyles[accent]

  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-lg border border-tf-border p-4">
        <div className="h-4 w-16 bg-tf-bg-hover rounded animate-pulse mb-3" />
        <div className="h-7 w-12 bg-tf-bg-hover rounded animate-pulse" />
      </div>
    )
  }

  return (
    <div className={cn(
      'bg-tf-bg-card rounded-lg border border-tf-border border-l-4 p-4',
      'hover:shadow-sm transition-all duration-200',
      styles.border
    )}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wide">{title}</p>
        <div className={cn('p-1.5 rounded-md', styles.bg)}>
          <Icon className={cn('w-3.5 h-3.5', styles.icon)} />
        </div>
      </div>
      <p className="text-2xl font-semibold text-tf-text">{count}</p>
    </div>
  )
}

export function CreativeSummaryCards({ summary, loading }: CreativeSummaryCardsProps) {
  return (
    <div className="space-y-4">
      {/* Main metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Spend"
          value={loading ? '—' : formatCurrency(summary?.total_spend || 0)}
          icon={Icons.DollarSign}
          loading={loading}
        />
        <MetricCard
          title="Total Revenue"
          value={loading ? '—' : formatCurrency(summary?.total_revenue || 0)}
          icon={Icons.TrendingUp}
          trend="up"
          loading={loading}
        />
        <MetricCard
          title="Avg ROAS"
          value={loading ? '—' : `${(summary?.avg_roas || 0).toFixed(2)}x`}
          icon={Icons.Target}
          trend={(summary?.avg_roas || 0) >= 1 ? 'up' : 'down'}
          loading={loading}
        />
        <MetricCard
          title="Active Creatives"
          value={loading ? '—' : formatNumber(summary?.active_creatives || 0)}
          icon={Icons.Image}
          loading={loading}
        />
      </div>

      {/* Performance status breakdown - modern cards with left border accent */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatusCard
          title="Winners"
          count={summary?.winners || 0}
          icon={Icons.Trophy}
          accent="emerald"
          loading={loading}
        />
        <StatusCard
          title="Rising Stars"
          count={summary?.rising_stars || 0}
          icon={Icons.Sparkles}
          accent="blue"
          loading={loading}
        />
        <StatusCard
          title="Testing"
          count={summary?.testing || 0}
          icon={Icons.FlaskConical}
          accent="amber"
          loading={loading}
        />
        <StatusCard
          title="Underperformers"
          count={summary?.underperformers || 0}
          icon={Icons.TrendingDown}
          accent="rose"
          loading={loading}
        />
      </div>
    </div>
  )
}

export default CreativeSummaryCards


